// Bridge Loan Calculator Logic
// Based on Refinance Cash-Out and Purchase templates from Simulation_1312 NW 15th Ter.xlsx

export const BRIDGE_CLOSING_COST_RATE = 0.045; // 4.5% default

// Transaction Types
export const BRIDGE_TRANSACTION_TYPES = {
  REFINANCE_CASHOUT: 'refinance_cashout',
  PURCHASE: 'purchase',
};

// Fee Rates (based on loan amount)
export const FEE_RATES = {
  LENDER_ORIGINATION: 0.02,           // 2% of loan amount
  BROKER_ORIGINATION: 0.02,           // 2% of loan amount
  LENDERS_TITLE_POLICY: 0.00081,      // 0.081% of loan amount
  OWNERS_TITLE_INSURANCE: 0.0042,     // 0.42% of loan amount (Purchase only)
  DOCUMENTARY_STAMPS: 0.0035,         // 0.35% of loan amount
  INTANGIBLE_TAX: 0.002,              // 0.2% of loan amount
};

// Default Fixed Fees (Loan Fees)
export const DEFAULT_FIXED_FEES = {
  TITLE_SEARCH: 275,
  LIEN_SEARCH: 200,
  TITLE_ENDORSEMENTS: 375,
  WIRE_FEE: 35,
  LENDER_DUE_DILIGENCE: 5000,
  PROCESSING_FEE: 5000,
};

/**
 * Calculate LTV for Bridge Loan
 * = Loan Amount / Property Value
 */
export const calculateBridgeLTV = (loanAmount, propertyValue) => {
  const value = parseFloat(propertyValue) || 0;
  if (value === 0) return 0;
  return (parseFloat(loanAmount) / value) * 100;
};

/**
 * Calculate Monthly Interest Payment (Interest-Only)
 * = (Loan Amount × Interest Rate) / 12
 */
export const calculateMonthlyPayment = (loanAmount, interestRate) => {
  const loan = parseFloat(loanAmount) || 0;
  const rate = parseFloat(interestRate) / 100 || 0;
  return (loan * rate) / 12;
};

/**
 * Calculate Lender Origination Fee from percentage
 * = Loan Amount × (percentage / 100)
 */
export const calculateLenderOriginationFee = (loanAmount, percentage = 2) => {
  const pct = parseFloat(percentage) || 0;
  return (parseFloat(loanAmount) || 0) * (pct / 100);
};

/**
 * Calculate Broker Origination Fee from percentage
 * = Loan Amount × (percentage / 100)
 */
export const calculateBrokerOriginationFee = (loanAmount, percentage = 2) => {
  const pct = parseFloat(percentage) || 0;
  return (parseFloat(loanAmount) || 0) * (pct / 100);
};

/**
 * Calculate Title Insurance (Lender's Policy)
 * = Loan Amount × 0.08125%
 */
export const calculateLendersTitlePolicy = (loanAmount) => {
  return (parseFloat(loanAmount) || 0) * FEE_RATES.LENDERS_TITLE_POLICY;
};

/**
 * Calculate Owner's Title Insurance
 * = Loan Amount × 0.42%
 */
export const calculateOwnersTitleInsurance = (loanAmount) => {
  return (parseFloat(loanAmount) || 0) * FEE_RATES.OWNERS_TITLE_INSURANCE;
};

/**
 * Calculate Documentary Stamps
 * = Loan Amount × 0.35%
 */
export const calculateDocumentaryStamps = (loanAmount) => {
  return (parseFloat(loanAmount) || 0) * FEE_RATES.DOCUMENTARY_STAMPS;
};

/**
 * Calculate Intangible Tax
 * = Loan Amount × 0.2%
 */
export const calculateIntangibleTax = (loanAmount) => {
  return (parseFloat(loanAmount) || 0) * FEE_RATES.INTANGIBLE_TAX;
};

/**
 * Calculate Interest Reserve
 * = Monthly Payment × Interest Reserve Months
 */
export const calculateBridgeInterestReserve = (monthlyPayment, reserveMonths) => {
  return (parseFloat(monthlyPayment) || 0) * (parseFloat(reserveMonths) || 0);
};

/**
 * Calculate Pre-Paid Interest
 * = Monthly Payment × (Days to First Payment / 30)
 */
export const calculatePrePaidInterest = (monthlyPayment, daysToFirstPayment = 15) => {
  return (parseFloat(monthlyPayment) || 0) * (daysToFirstPayment / 30);
};

/**
 * Calculate Cash Out Amount
 * = Loan Amount - Payoff Amount - Total Closing Costs
 */
export const calculateCashOut = (loanAmount, payoffAmount, totalClosingCosts) => {
  const loan = parseFloat(loanAmount) || 0;
  const payoff = parseFloat(payoffAmount) || 0;
  const closing = parseFloat(totalClosingCosts) || 0;
  return Math.max(loan - payoff - closing, 0);
};

/**
 * Main calculation function for Bridge Loan - Refinance Cash-Out
 * Based on Simulation_1312 NW 15th Ter.xlsx template
 */
export const calculateBridgeRefinanceMetrics = (inputs) => {
  // Parse inputs
  const propertyValue = parseFloat(inputs.propertyValue) || 0;
  const loanAmount = parseFloat(inputs.loanAmount) || 0;
  const interestRate = parseFloat(inputs.interestRate) || 0;
  const loanTerm = parseFloat(inputs.loanTerm) || 12;
  const payoffAmount = parseFloat(inputs.payoffAmount) || 0;
  const interestReserveMonths = parseFloat(inputs.interestReserveMonths) || 0;
  const prePaidInterestDays = parseFloat(inputs.prePaidInterestDays) || 15;
  
  // Loan Fees - use input values or defaults (editable)
  const lenderDueDiligence = inputs.lenderDueDiligence !== '' && inputs.lenderDueDiligence !== undefined 
    ? parseFloat(inputs.lenderDueDiligence) || 0 
    : DEFAULT_FIXED_FEES.LENDER_DUE_DILIGENCE;
  const processingFee = inputs.processingFee !== '' && inputs.processingFee !== undefined
    ? parseFloat(inputs.processingFee) || 0 
    : DEFAULT_FIXED_FEES.PROCESSING_FEE;
  const titleSearch = inputs.titleSearch !== '' && inputs.titleSearch !== undefined
    ? parseFloat(inputs.titleSearch) || 0 
    : DEFAULT_FIXED_FEES.TITLE_SEARCH;
  const titleEndorsements = inputs.titleEndorsements !== '' && inputs.titleEndorsements !== undefined
    ? parseFloat(inputs.titleEndorsements) || 0 
    : DEFAULT_FIXED_FEES.TITLE_ENDORSEMENTS;
  const lienSearch = inputs.lienSearch !== '' && inputs.lienSearch !== undefined
    ? parseFloat(inputs.lienSearch) || 0 
    : DEFAULT_FIXED_FEES.LIEN_SEARCH;
  const wireFee = inputs.wireFee !== '' && inputs.wireFee !== undefined
    ? parseFloat(inputs.wireFee) || 0 
    : DEFAULT_FIXED_FEES.WIRE_FEE;
  
  // Calculate LTV
  const ltv = calculateBridgeLTV(loanAmount, propertyValue);
  
  // Calculate monthly payment (interest-only)
  const monthlyPayment = calculateMonthlyPayment(loanAmount, interestRate);
  
  // Calculate origination fees from percentage inputs
  const lenderOriginationFeePercent = parseFloat(inputs.lenderOriginationFeePercent) || 0;
  const brokerOriginationFeePercent = parseFloat(inputs.brokerOriginationFeePercent) || 0;
  const lenderOriginationFee = calculateLenderOriginationFee(loanAmount, lenderOriginationFeePercent);
  const brokerOriginationFee = calculateBrokerOriginationFee(loanAmount, brokerOriginationFeePercent);
  const lendersTitlePolicy = calculateLendersTitlePolicy(loanAmount);
  const documentaryStamps = calculateDocumentaryStamps(loanAmount);
  const intangibleTax = calculateIntangibleTax(loanAmount);
  
  // Owner's Title Insurance - NOT applied for Refinance (only Purchase)
  const ownersTitleInsurance = 0;
  
  // Calculate reserves
  const interestReserve = calculateBridgeInterestReserve(monthlyPayment, interestReserveMonths);
  const prePaidInterest = calculatePrePaidInterest(monthlyPayment, prePaidInterestDays);
  
  // Total origination fees
  const totalOriginationFees = lenderOriginationFee + brokerOriginationFee;
  
  // Total title/closing fees (percentage-based)
  const totalPercentageFees = lendersTitlePolicy + documentaryStamps + intangibleTax;
  
  // Total fixed loan fees
  const totalFixedLoanFees = titleSearch + titleEndorsements + lienSearch + wireFee;
  
  // Lender due diligence & processing
  const totalLenderFees = lenderDueDiligence + processingFee;
  
  // Total fees (before pre-paids)
  const totalFees = totalOriginationFees + totalPercentageFees + totalFixedLoanFees + totalLenderFees;
  
  // Total pre-paids
  const totalPrePaids = interestReserve + prePaidInterest;
  
  // Total closing costs
  const totalClosingCosts = totalFees + totalPrePaids;
  
  // Calculate Cash Out (what borrower receives after payoff and closing costs)
  const cashOut = Math.max(loanAmount - payoffAmount - totalClosingCosts, 0);
  
  // Sources - Where the money comes from
  const totalSources = loanAmount;
  
  // Uses - Where the money goes
  const totalUses = payoffAmount + totalClosingCosts + cashOut;
  
  // Check if balanced (Sources should equal Uses)
  const isBalanced = Math.abs(totalSources - totalUses) < 0.01;
  
  // Equity requirement (if payoff + closing exceeds loan amount)
  const equityRequired = Math.max(payoffAmount + totalClosingCosts - loanAmount, 0);
  
  return {
    // Key metrics
    propertyValue,
    loanAmount,
    ltv,
    interestRate,
    loanTerm,
    monthlyPayment,
    monthlyInterest: monthlyPayment, // alias for consistency
    
    // Origination fees breakdown (includes percentages for display)
    lenderOriginationFee,
    lenderOriginationFeePercent,
    brokerOriginationFee,
    brokerOriginationFeePercent,
    totalOriginationFees,
    
    // Percentage-based fees (calculated from loan amount)
    lendersTitlePolicy,
    ownersTitleInsurance,
    documentaryStamps,
    intangibleTax,
    totalPercentageFees,
    
    // Fixed loan fees
    titleSearch,
    titleEndorsements,
    lienSearch,
    wireFee,
    totalFixedLoanFees,
    
    // Lender fees
    lenderDueDiligence,
    processingFee,
    totalLenderFees,
    
    // Combined fees
    totalFees,
    
    // Pre-paids breakdown
    interestReserve,
    prePaidInterest,
    prePaidInterestDays,
    totalPrePaids,
    
    // Total closing costs
    totalClosingCosts,
    
    // Transaction details
    payoffAmount,
    cashOut,
    
    // Sources and Uses
    totalSources,
    totalUses,
    isBalanced,
    equityRequired,
  };
};

/**
 * Calculation function for Bridge Loan - Purchase
 * Based on 5 Park SIM Bridge QM Purchase template
 */
export const calculateBridgePurchaseMetrics = (inputs) => {
  // Parse inputs - Purchase specific fields
  const purchasePrice = parseFloat(inputs.purchasePrice) || 0;
  const downPayment = parseFloat(inputs.downPayment) || 0;
  const afterRepairValue = parseFloat(inputs.afterRepairValue) || purchasePrice;
  const loanAmount = parseFloat(inputs.loanAmount) || 0;
  const interestRate = parseFloat(inputs.interestRate) || 0;
  const loanTerm = parseFloat(inputs.loanTerm) || 24;
  const interestReserveMonths = parseFloat(inputs.interestReserveMonths) || 0;
  const prePaidInterestDays = parseFloat(inputs.prePaidInterestDays) || 15;
  
  // Loan Fees - use input values or defaults (editable)
  const lenderDueDiligence = inputs.lenderDueDiligence !== '' && inputs.lenderDueDiligence !== undefined 
    ? parseFloat(inputs.lenderDueDiligence) || 0 
    : DEFAULT_FIXED_FEES.LENDER_DUE_DILIGENCE;
  const processingFee = inputs.processingFee !== '' && inputs.processingFee !== undefined
    ? parseFloat(inputs.processingFee) || 0 
    : DEFAULT_FIXED_FEES.PROCESSING_FEE;
  const titleSearch = inputs.titleSearch !== '' && inputs.titleSearch !== undefined
    ? parseFloat(inputs.titleSearch) || 0 
    : DEFAULT_FIXED_FEES.TITLE_SEARCH;
  const titleEndorsements = inputs.titleEndorsements !== '' && inputs.titleEndorsements !== undefined
    ? parseFloat(inputs.titleEndorsements) || 0 
    : DEFAULT_FIXED_FEES.TITLE_ENDORSEMENTS;
  const lienSearch = inputs.lienSearch !== '' && inputs.lienSearch !== undefined
    ? parseFloat(inputs.lienSearch) || 0 
    : DEFAULT_FIXED_FEES.LIEN_SEARCH;
  const wireFee = inputs.wireFee !== '' && inputs.wireFee !== undefined
    ? parseFloat(inputs.wireFee) || 0 
    : DEFAULT_FIXED_FEES.WIRE_FEE;
  
  // Calculate LTVs
  const purchaseLTV = purchasePrice > 0 ? (loanAmount / purchasePrice) * 100 : 0;
  const arvLTV = afterRepairValue > 0 ? (loanAmount / afterRepairValue) * 100 : 0;
  
  // Calculate monthly payment (interest-only)
  const monthlyPayment = calculateMonthlyPayment(loanAmount, interestRate);
  
  // Calculate origination fees from percentage inputs
  const lenderOriginationFeePercent = parseFloat(inputs.lenderOriginationFeePercent) || 0;
  const brokerOriginationFeePercent = parseFloat(inputs.brokerOriginationFeePercent) || 0;
  const lenderOriginationFee = calculateLenderOriginationFee(loanAmount, lenderOriginationFeePercent);
  const brokerOriginationFee = calculateBrokerOriginationFee(loanAmount, brokerOriginationFeePercent);
  const lendersTitlePolicy = calculateLendersTitlePolicy(loanAmount);
  const documentaryStamps = calculateDocumentaryStamps(loanAmount);
  const intangibleTax = calculateIntangibleTax(loanAmount);
  
  // Owner's Title Insurance - ONLY applies for Purchase transactions
  const ownersTitleInsurance = calculateOwnersTitleInsurance(loanAmount);
  
  // Calculate reserves
  const interestReserve = calculateBridgeInterestReserve(monthlyPayment, interestReserveMonths);
  const prePaidInterest = calculatePrePaidInterest(monthlyPayment, prePaidInterestDays);
  
  // Total origination fees
  const totalOriginationFees = lenderOriginationFee + brokerOriginationFee;
  
  // Total percentage-based fees (calculated from loan amount)
  const totalPercentageFees = lendersTitlePolicy + ownersTitleInsurance + documentaryStamps + intangibleTax;
  
  // Total fixed loan fees
  const totalFixedLoanFees = titleSearch + titleEndorsements + lienSearch + wireFee;
  
  // Lender due diligence & processing
  const totalLenderFees = lenderDueDiligence + processingFee;
  
  // Total fees (before pre-paids)
  const totalFees = totalOriginationFees + totalPercentageFees + totalFixedLoanFees + totalLenderFees;
  
  // Total pre-paids
  const totalPrePaids = interestReserve + prePaidInterest;
  
  // Total closing costs
  const totalClosingCosts = totalFees + totalPrePaids;
  
  // Cash to close = Purchase Price + Closing Costs - Loan Amount
  const cashToClose = Math.max(purchasePrice + totalClosingCosts - loanAmount, 0);
  
  // Sources
  const totalSources = loanAmount + downPayment + (cashToClose - downPayment > 0 ? cashToClose - downPayment : 0);
  
  // Uses
  const totalUses = purchasePrice + totalClosingCosts;
  
  // Equity required
  const equityRequired = cashToClose;
  
  return {
    // Key metrics
    purchasePrice,
    afterRepairValue,
    loanAmount,
    purchaseLTV,
    arvLTV,
    ltv: purchaseLTV, // alias for consistency
    interestRate,
    loanTerm,
    monthlyPayment,
    monthlyInterest: monthlyPayment, // alias for consistency
    downPayment,
    
    // Origination fees breakdown (includes percentages for display)
    lenderOriginationFee,
    lenderOriginationFeePercent,
    brokerOriginationFee,
    brokerOriginationFeePercent,
    totalOriginationFees,
    
    // Percentage-based fees (calculated from loan amount)
    lendersTitlePolicy,
    ownersTitleInsurance,
    documentaryStamps,
    intangibleTax,
    totalPercentageFees,
    
    // Fixed loan fees
    titleSearch,
    titleEndorsements,
    lienSearch,
    wireFee,
    totalFixedLoanFees,
    
    // Lender fees
    lenderDueDiligence,
    processingFee,
    totalLenderFees,
    
    // Combined fees
    totalFees,
    
    // Pre-paids breakdown
    interestReserve,
    prePaidInterest,
    prePaidInterestDays,
    totalPrePaids,
    
    // Total closing costs
    totalClosingCosts,
    
    // Cash requirements
    cashToClose,
    equityRequired,
    
    // Sources and Uses
    totalSources,
    totalUses,
  };
};
